import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Carte KPI réutilisable pour le module Reporting (Phase 12).
 *
 *  Affiche un libellé, une valeur principale (chiffre/string) et un
 *  sous-texte/tendance optionnel. Peut être enveloppée dans un `<Link>`.
 */
export type KpiTone =
  | 'green'
  | 'red'
  | 'orange'
  | 'blue'
  | 'purple'
  | 'gray'
  | 'primary'
  | 'teal'
  | 'yellow';

const TONE_TEXT: Record<KpiTone, string> = {
  green: 'text-green-600',
  red: 'text-red-600',
  orange: 'text-orange-600',
  blue: 'text-blue-600',
  purple: 'text-purple-600',
  gray: 'text-gray-700',
  primary: 'text-primary-600',
  teal: 'text-teal-600',
  yellow: 'text-yellow-600',
};

export interface KpiCardProps {
  label: string;
  value: string | number;
  icon?: ComponentType<{ className?: string }>;
  tone?: KpiTone;
  subtext?: ReactNode;
  to?: string;
}

export function KpiCard({ label, value, icon: Icon, tone = 'gray', subtext, to }: KpiCardProps) {
  const toneClass = TONE_TEXT[tone];
  const content = (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow transition-shadow h-full">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500 truncate pr-2">{label}</p>
        {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${toneClass}`} />}
      </div>
      <p className={`text-2xl font-bold mt-1 ${toneClass}`}>{value}</p>
      {subtext && <div className="mt-1 text-xs text-gray-500">{subtext}</div>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}
